// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import { showSnackBar } from "../../snackbar/actions"
import * as TimelineActions from "../../timeline/actions"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { Code, executorMutation, Init, isConnectError, timesheetFieldService } from "../../../app/rpc"
import {
  UpdateTimesheetFieldRequestSchema,
  TimesheetField,
  ListTimesheetFieldsRequestSchema,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type UpdateTimesheetFieldRpc = (request: Init<typeof UpdateTimesheetFieldRequestSchema>) => Observable<TimesheetField>

export const updateTimesheetField = (updateTimesheetFieldRpc: UpdateTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.updateTimesheetField.match),
      map(action => action.payload),
      mergeMap(payload =>
        updateTimesheetFieldRpc(payload.request).pipe(
          mergeMap(response => {
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            const actions: Action[] = [
              TimelineSlice.setTimesheetField(response),
            ]
            if (payload.request.update?.defaultOptionId) {
              actions.push(
                Slice.setDefaultTimesheetFieldOptionId({
                  defaultOptionId: payload.request.update.defaultOptionId,
                  timesheetFieldId: payload.request.id ?? "",
                })
              )
            }
            return concat(actions)
          }),
          catchError(e => handleError(e, payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Actions.Payload<Init<typeof UpdateTimesheetFieldRequestSchema>>): Observable<Action> => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      return concat([
        TimelineActions.listTimesheetFields({
          request: create(ListTimesheetFieldsRequestSchema, { siteId: payload.request.siteId }),
          includeOptions: true,
        }),
        showSnackBar({
          message: t({
            id: "msg_timesheet_field_not_found",
            message: "Timesheet field not found.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_update_timesheet_field_permission_denied",
          message: "Failed to update timesheet field. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_update_timesheet_field_failed",
      message: "Failed to update timesheet field. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const updateTimesheetFieldRpc = (request: Init<typeof UpdateTimesheetFieldRequestSchema>): Observable<TimesheetField> =>
  executorMutation(() => timesheetFieldService.updateTimesheetField(create(UpdateTimesheetFieldRequestSchema, request)))

export default updateTimesheetField(updateTimesheetFieldRpc)
